import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { PaginatedPaymentMethodList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface QueryKeyType {
  pageParam?: UrlParams;
}

interface IUsePaymentMethods {
  type?: string;
  athleteId?: string;
  refetchInterval?: number;
  enabled?: boolean;
  stripePaymentMethodId?: string;
}

function usePaymentMethods({
  athleteId,
  type,
  stripePaymentMethodId,
  refetchInterval,
  enabled = true,
}: IUsePaymentMethods): UseInfiniteQueryResult<AxiosResponse<PaginatedPaymentMethodList>> {
  const fetchPaymentMethods = useCallback(
    ({
      pageParam = { limit: '20', offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedPaymentMethodList>> => {
      // pageParam is react-query state (the parsed `next` URL of the previous page):
      // build a new object instead of writing the filters back into it.
      const params: UrlParams = {
        ...pageParam,
        ...(athleteId !== undefined && { athlete: athleteId }),
        ...(type !== undefined && { type }),
        ...(stripePaymentMethodId !== undefined && { stripe_payment_method_id: stripePaymentMethodId }),
      };
      return client.get(`payment-methods/?${encodeUrlParams(params)}`);
    },
    [athleteId, type, stripePaymentMethodId],
  );

  return useInfiniteQuery(['paymentMethods', athleteId, type, stripePaymentMethodId], fetchPaymentMethods, {
    getNextPageParam: page => {
      return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
    },
    enabled: enabled && !!athleteId,
    refetchInterval: refetchInterval,
    refetchIntervalInBackground: !!refetchInterval,
  });
}

export default usePaymentMethods;
